import React, { useEffect, useState } from "react";
import {
  Crown,
  Star,
  Rocket,
  Users,
  MessageCircle,
  Layers,
} from "lucide-react";
import { toast } from "react-toastify";
import { getAllPlan } from "../../../api/apiFunction/planServices";
import { requestOrgPlanUpgrade } from "../../../api/apiFunction/orgServices";
import ConfirmModal from "../../ui/ConfirmModal";

const planIcons = {
  free: <Crown className="text-yellow-400" size={28} />,
  pro: <Star className="text-blue-500" size={28} />,
  enterprise: <Rocket className="text-purple-500" size={28} />,
};

const planColors = {
  free: "from-yellow-400 to-yellow-500",
  pro: "from-blue-500 to-blue-600",
  enterprise: "from-purple-500 to-purple-600",
};

const PlanFeature = ({ icon: Icon, label, value, planType = "free" }) => {
  const colors = {
    free: "yellow",
    pro: "blue",
    enterprise: "purple",
  };
  const color = colors[planType] || "gray";

  return (
    <div className="flex items-center justify-between py-2 px-3 rounded-lg bg-bg-50 dark:bg-bg-70 hover:bg-opacity-50 transition-colors">
      <div className="flex items-center space-x-2">
        {Icon && <Icon className={`text-${color}-500`} size={16} />}
        <span>{label}:</span>
        <span className="font-medium">{value}</span>
      </div>
    </div>
  );
};

const PlansSkeleton = () => (
  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
    {[1, 2, 3].map((_, idx) => (
      <div
        key={idx}
        className="border border-bd-50 rounded-xl p-6 flex flex-col h-full animate-pulse bg-bg-60"
      >
        <div className="w-14 h-14 rounded-xl mb-4 bg-bg-40" />
        <div className="h-6 bg-bg-40 rounded w-1/2 mb-4" />
        <div className="h-8 bg-bg-40 rounded w-1/3 mb-4" />
        <div className="space-y-3 my-4 border-t border-bd-50 pt-4">
          <div className="h-4 bg-bg-40 rounded w-3/4" />
          <div className="h-4 bg-bg-40 rounded w-2/3" />
          <div className="h-4 bg-bg-40 rounded w-1/2" />
        </div>
        <div className="mt-auto pt-4">
          <div className="h-10 bg-bg-40 rounded w-full" />
        </div>
      </div>
    ))}
  </div>
);

const Plans = ({ onChangePlan }) => {
  const user = JSON.parse(localStorage.getItem("user"));
  const [apiPlans, setApiPlans] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [plansLoading, setPlansLoading] = useState(true);
  const [requestMessage, setRequestMessage] = useState("");
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [agreementChecked, setAgreementChecked] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    const fetchPlans = async () => {
      setPlansLoading(true);
      const res = await getAllPlan();
      if (res?.data) {
        setApiPlans(res.data);
      }
      setPlansLoading(false);
    };
    fetchPlans();
  }, []);

  const displayPlans = apiPlans;

  const handleSelectPlan = (plan) => {
    setSelectedPlan(plan);
    setRequestMessage("");
    setAgreementChecked(false);
    setErrorMsg("");
    setShowRequestModal(true);
  };

  const handleRequestUpgrade = async () => {
    if (!selectedPlan) return;
    if (!requestMessage.trim()) {
      setErrorMsg("Please enter a message for your request.");
      return;
    }
    setLoading(true);
    setErrorMsg("");
    try {
      const response = await requestOrgPlanUpgrade({
        organizationId: user.organization._id,
        planName: selectedPlan.name,
        requestMessage,
      });
      if (response?.data) {
        if (response.data.error) {
          setErrorMsg(response.data.error);
        } else {
          toast.success("Plan upgrade request submitted! Awaiting admin approval.");
          setShowRequestModal(false);
          setSelectedPlan(null);
          setRequestMessage("");
          setAgreementChecked(false);
          setErrorMsg("");
        }
      } else {
        throw new Error(response?.data?.error || "Failed to submit request.");
      }
    } catch (err) {
      setErrorMsg(err.message || "Failed to submit request.");
    } finally {
      setLoading(false);
    }
  };

  const agreementText = (
    <div className="text-left">
      <div className="font-semibold mb-1">Agreement:</div>
      <ul className="list-disc pl-5 space-y-1 text-xs font-normal">
        <li>Your organization is requesting to upgrade to the selected plan and will be subject to the terms, pricing, and limitations of that plan upon approval.</li>
        <li>The upgrade will not take effect until reviewed and approved by a platform administrator.</li>
        <li>Any changes in billing, employee limits, chat/thread limits, or other plan features will apply once the upgrade is approved.</li>
        <li>You are authorized to make this request on behalf of your organization.</li>
        <li>This request is binding and may result in changes to your organization's subscription and associated charges.</li>
      </ul>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Current Plan Section */}
      <div className="rounded-lg p-y-6 shadow-sm">
        <h2 className="text-xl font-semibold mb-4">Pricing Plans</h2>
        <div className="flex items-center justify-between p-y-4 bg-bg-50 rounded-lg">
          {onChangePlan && (
            <button
              onClick={onChangePlan}
              className="px-4 py-2 bg-ac-02 text-white dark:text-black rounded-md hover:bg-ac-01 transition-colors"
            >
              Change Plan
            </button>
          )}
        </div>
      </div>

      {/* Available Plans */}
      {plansLoading ? (
        <PlansSkeleton />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {displayPlans.map((plan) => {
            const planName = plan.name?.toLowerCase();
            const isCurrent = plan._id === user?.plan?._id;
            const gradient = planColors[planName] || "from-gray-500 to-gray-600";

            return (
              <div
                key={plan._id}
                className={`relative border rounded-xl p-6 flex flex-col h-full transition-all duration-300 hover:shadow-lg ${
                  isCurrent
                    ? "border-2 border-ac-02 bg-bg-60/50"
                    : "border-bd-50 bg-bg-60 hover:border-bd-100"
                }`}
              >
                {isCurrent && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-ac-02 text-white dark:text-black text-xs font-medium px-3 py-1 rounded-full">
                    Current Plan
                  </div>
                )}

                {/* Plan Icon */}
                <div
                  className={`w-14 h-14 rounded-xl mb-4 flex items-center justify-center ${gradient} bg-gradient-to-r text-white`}
                >
                  {React.cloneElement(planIcons[planName] || <Crown />, {
                    className: "text-white",
                    size: 28,
                  })}
                </div>

                {/* Plan Name and Price */}
                <div className="flex justify-between items-start mb-4">
                  <h3 className="text-xl font-bold text-fg-90">
                    {plan.name?.charAt(0).toUpperCase() + plan.name?.slice(1)}
                  </h3>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-fg-90">
                      ${plan.price}
                      <span className="text-sm font-normal text-fg-60">
                        /month
                      </span>
                    </div>
                  </div>
                </div>

                {/* Plan Features */}
                <div className="space-y-3 my-4 border-t border-bd-50 pt-4">
                  <PlanFeature
                    icon={Users}
                    label="Employee Limit"
                    value={plan.employeeLimit || "Unlimited"}
                    planType={planName}
                  />
                  <PlanFeature
                    icon={MessageCircle}
                    label="Chat Limit"
                    value={
                      plan.chatLimit !== undefined
                        ? `${plan.chatLimit} per month`
                        : "Unlimited"
                    }
                    planType={planName}
                  />
                  <PlanFeature
                    icon={Layers}
                    label="Thread Limit"
                    value={
                      plan.threadLimit !== undefined
                        ? plan.threadLimit
                        : "Unlimited"
                    }
                    planType={planName}
                  />
                </div>

                {/* Action Button */}
                <div className="mt-auto pt-4">
                  <button
                    onClick={() => !isCurrent && handleSelectPlan(plan)}
                    disabled={isCurrent || loading}
                    className={`w-full py-3 px-6 rounded-lg font-medium transition-all flex items-center justify-center space-x-2
                      ${
                        isCurrent
                          ? "bg-bg-60 text-fg-60 cursor-default border border-bd-50"
                          : `bg-gradient-to-r ${gradient} text-white hover:opacity-90 hover:shadow-lg transform hover:-translate-y-0.5`
                      }`}
                  >
                    {isCurrent ? "Current Plan" : "Request Upgrade"}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {/* Request Message Modal */}
      {showRequestModal && (
      <ConfirmModal
          open={showRequestModal}
          onClose={() => {
            setShowRequestModal(false);
            setErrorMsg("");
          }}
          onConfirm={handleRequestUpgrade}
        loading={loading}
          hideActions={true}
          message={
            <div>
              <div className="mb-2 font-semibold text-lg">
                Request Plan Upgrade: {selectedPlan?.name}
              </div>
              {errorMsg && (
                <div className="mb-2 text-xs text-red-600 font-medium text-left">{errorMsg}</div>
              )}
              {/* Agreement Section - visually distinct, no icon */}
              <div className="bg-bg-50 dark:bg-bg-70 border border-bd-50 rounded-lg p-4 mb-3 text-left">
                <div className="font-semibold text-sm mb-1 text-fg-90">Agreement</div>
                <ul className="list-disc pl-5 space-y-1 text-xs font-normal text-fg-60">
                  <li>Your organization is requesting to upgrade to the selected plan and will be subject to the terms, pricing, and limitations of that plan upon approval.</li>
                  <li>The upgrade will not take effect until reviewed and approved by a platform administrator.</li>
                  <li>Any changes in billing, employee limits, chat/thread limits, or other plan features will apply once the upgrade is approved.</li>
                  <li>You are authorized to make this request on behalf of your organization.</li>
                  <li>This request is binding and may result in changes to your organization's subscription and associated charges.</li>
                </ul>
              </div>
              {/* Textarea below agreement */}
              <textarea
                className="w-full min-h-[80px] border border-bd-50 rounded-lg p-2 text-sm focus:outline-none focus:ring-2 focus:ring-ac-02 mb-3 bg-bg-60"
                placeholder="Enter your message for the admin..."
                value={requestMessage}
                onChange={e => setRequestMessage(e.target.value)}
                disabled={loading}
              />
              <label className="flex items-center gap-2 mb-4">
                <input
                  type="checkbox"
                  checked={agreementChecked}
                  onChange={e => setAgreementChecked(e.target.checked)}
                  disabled={loading}
                  className="form-checkbox h-4 w-4 text-ac-02 border-gray-300 rounded"
                />
                <span className="text-xs text-fg-60">I have read and agree to the above terms.</span>
              </label>
              {/* Action Buttons aligned right, no extra bottom margin, normal font */}
              <div className="pt-2 flex justify-end space-x-3 mb-0">
                <button
                  type="button"
                  onClick={() => {
                    setShowRequestModal(false);
                    setErrorMsg("");
                  }}
                  className={`px-4 py-2 text-xs font-medium transition-colors ${loading ? 'text-fg-40' : 'text-fg-50 hover:text-fg-40'}`}
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleRequestUpgrade}
                  disabled={!agreementChecked || loading}
                  className={`py-2 px-4 rounded-lg transition-colors flex items-center justify-center text-xs font-normal ${
                    !agreementChecked || loading
                      ? 'bg-bg-40 text-fg-40 cursor-not-allowed'
                      : 'bg-ac-02 hover:bg-ac-01 text-white dark:text-black '
                  }`}
                >
                  {loading ? 'Requesting...' : 'Request'}
                </button>
              </div>
            </div>
          }
          confirmDisabled={!agreementChecked || loading}
        />
      )}
    </div>
  );
};

export default Plans;
